import { getApiBaseUrl } from './config';

const REQUEST_TIMEOUT_MS = 15000;

/**
 * Error thrown for any failed Monera API request
 */
export class MoneraAPIError extends Error {
  constructor(message, { status = 0, code = null, fields = null, retryAfterSeconds = 60, timedOut = false, fallbackNotes = [] } = {}) {
    super(message);
    this.name = 'MoneraAPIError';
    this.status = status;
    this.code = code;
    this.fields = fields;
    this.retryAfterSeconds = retryAfterSeconds;
    this.timedOut = timedOut;
    this.fallbackNotes = fallbackNotes;
  }
}

/**
 * Wrapper for the Monera API
 */
export default class MoneraAPI {
  static methods = Object.freeze({
    GET: 'GET',
    POST: 'POST',
    PUT: 'PUT',
    DELETE: 'DELETE'
  });

  /**
   * Generic request method to handle errors and return JSON
   * @param {string} method HTTP method
   * @param {string} endpoint Path relative to the API base URL
   * @param {object} data Request body
   * @param {AbortSignal} signal Optional signal to cancel a superseded request
   */
  static async _request(method, endpoint, data, signal) {
    const base = getApiBaseUrl().replace(/\/+$/, '');
    const url = `${base}/${endpoint}`;

    const controller = new AbortController();
    let timedOut = false;
    const timeout = setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, REQUEST_TIMEOUT_MS);
    const abortFromCaller = () => controller.abort();

    if (signal) {
      if (signal.aborted) {
        controller.abort();
      } else {
        signal.addEventListener('abort', abortFromCaller);
      }
    }

    const opts = {
      method,
      mode: 'cors',
      credentials: 'omit',
      signal: controller.signal,
    };

    if (data) {
      opts.headers = { 'Content-Type': 'application/json' };
      opts.body = JSON.stringify(data);
    }

    try {
      // The timeout covers reading the body too: fetch() resolves once headers arrive
      const resp = await fetch(url, opts);

      if (!(resp.headers.get('content-type') || '').includes('application/json')) {
        throw new MoneraAPIError('The quote service returned an unexpected response', { status: resp.status });
      }

      let body;

      try {
        body = await resp.json();
      } catch (err) {
        if (err.name === 'AbortError') {
          throw err;
        }

        throw new MoneraAPIError('The quote service returned an unexpected response', { status: resp.status });
      }

      if (!resp.ok || !body.data) {
        throw new MoneraAPIError(body.message || 'Unable to calculate quote', {
          status: resp.status,
          code: body.code || null,
          fields: body.errors || null,
          fallbackNotes: body.fallback_notes,
          retryAfterSeconds: Number(resp.headers.get('Retry-After')) || 60,
        });
      }

      return body;
    } catch (err) {
      // Babel 6 can't subclass Error, so instanceof fails; match the name instead
      if (err.name === 'MoneraAPIError') {
        throw err;
      }

      // A superseded request was cancelled by the caller; stay silent
      if (err.name === 'AbortError' && !timedOut) {
        throw err;
      }

      throw new MoneraAPIError('Unable to reach the quote service', { timedOut });
    } finally {
      clearTimeout(timeout);

      if (signal) {
        signal.removeEventListener('abort', abortFromCaller);
      }
    }
  }

  /**
   * API endpoint for the active resorts and supported loan terms
   * @param {object} query Optional quote inputs; the terms then reflect what they qualify for
   * @param {AbortSignal} signal Optional signal to cancel a superseded request
   */
  static async getLoanConfig(query, signal) {
    const search = query ? `?${new URLSearchParams(query).toString()}` : '';
    const result = await this._request(this.methods.GET, `monera/loans/config${search}`, null, signal);
    return result.data;
  }

  /**
   * API endpoint to calculate closing costs
   * @param {object} data Data to send to the API
   * @param {AbortSignal} signal Optional signal to cancel a superseded request
   */
  static async calculateClosingCosts(data, signal) {
    const result = await this._request(this.methods.POST, 'monera/loans/calculate', data, signal);
    return result.data;
  }
}



// WEBPACK FOOTER //
// blocks/quote-calculator/app/api.js